import { AppNode, ProcessDefinition, Edge, NodeCategory } from '../../types';
import { getNodeTypes } from '../nodeRegistry';
import { ProcessLookup } from './types';

// Shape of edge.data fields the editor attaches to sequence-flow edges.
// Not all of these are modelled on the public Edge type, so we narrow the
// cast to this shape rather than spreading `as any` across the file.
type EditorEdgeData = { isAssociation?: boolean; isRecursive?: boolean };
const edgeData = (e: Edge): EditorEdgeData | undefined =>
  (e as Edge & { data?: EditorEdgeData }).data;

/**
 * Build O(1) lookup maps for a process. Called once per rule-run per process
 * and passed to every rule, so rules should never rescan edges manually.
 */
/**
 * Association edges (parent activity → boundary event) are rendered for
 * visual linkage only — they represent the parentNode attachment, not a
 * sequence flow. Validation should ignore them everywhere.
 */
export const isAssociationEdge = (e: Edge): boolean =>
  e.sourceHandle === 'boundary' || edgeData(e)?.isAssociation === true;

export const buildLookup = (process: ProcessDefinition): ProcessLookup => {
  const nodesById = new Map<string, AppNode>();
  const incomingByNode = new Map<string, Edge[]>();
  const outgoingByNode = new Map<string, Edge[]>();
  const edgesById = new Map<string, Edge>();

  process.nodes.forEach(n => {
    nodesById.set(n.id, n);
    incomingByNode.set(n.id, []);
    outgoingByNode.set(n.id, []);
  });

  process.edges.forEach(e => {
    edgesById.set(e.id, e);
    if (isAssociationEdge(e)) return; // Skip visual-only boundary links.
    const outgoing = outgoingByNode.get(e.source);
    if (outgoing) outgoing.push(e);
    const incoming = incomingByNode.get(e.target);
    if (incoming) incoming.push(e);
  });

  return { nodesById, incomingByNode, outgoingByNode, edgesById };
};

export const getIncoming = (lookup: ProcessLookup, nodeId: string): Edge[] =>
  lookup.incomingByNode.get(nodeId) ?? [];

export const getOutgoing = (lookup: ProcessLookup, nodeId: string): Edge[] =>
  lookup.outgoingByNode.get(nodeId) ?? [];

export const getNode = (lookup: ProcessLookup, nodeId: string): AppNode | undefined =>
  lookup.nodesById.get(nodeId);

/**
 * Normal (non-recursive, non-boundary-attachment) incoming/outgoing counts.
 * Boundary attachment is NOT a sequence flow — boundary events are wired via
 * parentNode, not edges. Recursive edges (edge.data.isRecursive) do NOT count
 * toward cardinality per engine semantics (`CanJoin = inputs - recursive`).
 */
export const getFlowIncoming = (lookup: ProcessLookup, nodeId: string): Edge[] =>
  getIncoming(lookup, nodeId).filter(e => !edgeData(e)?.isRecursive);

export const getFlowOutgoing = (lookup: ProcessLookup, nodeId: string): Edge[] =>
  getOutgoing(lookup, nodeId).filter(e => !edgeData(e)?.isRecursive);

/**
 * Editor node-type families. Uses the NodeCategory registered in nodeRegistry
 * as the authoritative mapping. The fallback suffix checks cover types the
 * registry has not registered (e.g. engine-internal variants that slip through
 * import).
 */
export type NodeFamily =
  | 'StartEvent'
  | 'EndEvent'
  | 'Activity'
  | 'IntermediateCatching'
  | 'IntermediateThrow'
  | 'BoundaryEvent'
  | 'Gateway'
  | 'Unknown';

export const ACTIVITY_TYPES = new Set([
  'ScriptTask',
  'UserTask',
  'ServiceTask',
  'ServiceTaskREST',
  'ServiceTaskEmail',
  'ServiceTaskSMS',
  'ServiceTaskDB',
  'ServiceTaskLLM',
  'ServiceTaskMCP',
  'ServiceTaskRules',
  'SubProcess',
  'CallActivity',
]);

export const GATEWAY_TYPES = new Set([
  'XorGateway',
  'InclusiveGateway',
  'ParallelGateway',
]);

export const getFamily = (type: string | undefined): NodeFamily => {
  if (!type) return 'Unknown';

  const registry = getNodeTypes();
  const cat = registry[type]?.category;

  if (cat === NodeCategory.Start) return 'StartEvent';
  if (cat === NodeCategory.End) return 'EndEvent';
  if (cat === NodeCategory.Gateway) return 'Gateway';
  if (cat === NodeCategory.Boundary) return 'BoundaryEvent';
  if (cat === NodeCategory.Task || cat === NodeCategory.Activity) return 'Activity';
  if (cat === NodeCategory.Intermediate) {
    return type.includes('Throw') ? 'IntermediateThrow' : 'IntermediateCatching';
  }

  // Suffix fallbacks for types not in registry
  if (type.endsWith('StartEvent')) return 'StartEvent';
  if (type.endsWith('EndEvent')) return 'EndEvent';
  if (type.endsWith('BoundaryEvent')) return 'BoundaryEvent';
  if (type.endsWith('Gateway')) return 'Gateway';
  if (type.startsWith('IntermediateThrow')) return 'IntermediateThrow';
  if (type.startsWith('IntermediateCatch')) return 'IntermediateCatching';
  if (ACTIVITY_TYPES.has(type)) return 'Activity';

  return 'Unknown';
};

export const isStartEvent = (n: AppNode | undefined) =>
  !!n && getFamily(n.type) === 'StartEvent';

export const isEndEvent = (n: AppNode | undefined) =>
  !!n && getFamily(n.type) === 'EndEvent';

export const isActivity = (n: AppNode | undefined) =>
  !!n && getFamily(n.type) === 'Activity';

export const isBoundaryEvent = (n: AppNode | undefined) =>
  !!n && getFamily(n.type) === 'BoundaryEvent';

export const isGateway = (n: AppNode | undefined) =>
  !!n && getFamily(n.type) === 'Gateway';

export const isIntermediate = (n: AppNode | undefined) => {
  const f = n && getFamily(n.type);
  return f === 'IntermediateCatching' || f === 'IntermediateThrow';
};

export const isForkGateway = (n: AppNode | undefined) =>
  isGateway(n) && !n?.data?.isMerging;

export const isMergeGateway = (n: AppNode | undefined) =>
  isGateway(n) && !!n?.data?.isMerging;

/**
 * Formatted label for messages — tries registry label, falls back to id.
 */
export const labelOf = (n: AppNode | undefined): string => {
  if (!n) return '<missing>';
  const typeLabel = getNodeTypes()[n.type ?? '']?.label ?? n.type ?? 'node';
  const displayLabel = (n.data as { label?: string } | undefined)?.label;
  return displayLabel ? `${displayLabel} (${typeLabel})` : `${typeLabel} ${n.id.slice(0, 8)}`;
};
